import { useCallback, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import Card from 'app/frontend/base/components/Card'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useDefaultSetups, usePageInfo } from 'app/frontend/base/config/queries'
import {
  updateDefaultSetupSelection,
  upsertDefaultSetup,
} from 'app/frontend/base/api/default_setups'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import EventActivityBlock from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/EventActivityBlock'
import EventActivitySelectionCount from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/EventActivitySelectionCount'
import DefaultSetupProviders from './DefaultSetupProviders'
import {
  buildDefaultSetupActivity,
  buildDefaultSetupEntries,
} from './defaultSetupEntries'
import useDefaultSetupOptimisticUpdate, {
  applyDefaultSetupSelection,
} from './useDefaultSetupOptimisticUpdate'

type Props = {
  encounter: wowaudit.Encounter
  instance?: wowaudit.Instance
  difficulty: Difficulty
  canEdit: boolean
}

const DefaultSetupCard = ({
  encounter,
  instance,
  difficulty,
  canEdit,
}: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const queryKey = ['defaultSetups', team.id] as const
  const { data: setups } = useDefaultSetups(team.id)
  const optimisticUpdate = useDefaultSetupOptimisticUpdate(queryKey)
  const setup = useMemo(
    () =>
      setups?.find(
        (entry) =>
          entry.encounterId === encounter.id && entry.difficulty === difficulty,
      ),
    [difficulty, encounter.id, setups],
  )
  const enabled = setup?.enabled ?? false
  const canInteract = canEdit && enabled && (setup?.id ?? 0) > 0

  const activity = useMemo(
    () =>
      buildDefaultSetupActivity({
        encounter,
        instance,
        difficulty,
        gameVersion,
      }),
    [difficulty, encounter, gameVersion, instance],
  )
  const entries = useMemo(
    () =>
      buildDefaultSetupEntries({
        team,
        members: team.members ?? [],
        activityId: activity.id,
        selections: setup?.selections ?? {},
      }),
    [activity.id, setup?.selections, team],
  )

  const upsertMutation = useMutation({
    mutationFn: (nextEnabled: boolean) =>
      upsertDefaultSetup(team.id, {
        encounter_id: encounter.id,
        difficulty,
        enabled: nextEnabled,
      }),
    onMutate: async (nextEnabled) => {
      const previous = await optimisticUpdate.snapshot()
      return optimisticUpdate.registerOptimisticPatch(previous, (current) => {
        const existing = current.find(
          (entry) =>
            entry.encounterId === encounter.id &&
            entry.difficulty === difficulty,
        )
        if (existing) {
          return current.map((entry) =>
            entry.id === existing.id
              ? { ...entry, enabled: nextEnabled }
              : entry,
          )
        }
        return [
          ...current,
          {
            id: -1,
            encounterId: encounter.id,
            difficulty,
            enabled: nextEnabled,
            selections: {},
          },
        ]
      })
    },
    onError: (_error, _variables, context) => {
      optimisticUpdate.rollback(context)
    },
    onSuccess: (data, _variables, context) => {
      optimisticUpdate.applyServerUpdate(data, context)
    },
  })

  const selectionMutation = useMutation({
    mutationFn: (selection: {
      character_id: number
      selected?: boolean
      role?: string
      spec_id?: number | null
      alt_character_id?: number
    }) => {
      if (!setup || setup.id <= 0) {
        return Promise.reject(new Error('default_setup_missing'))
      }
      return updateDefaultSetupSelection(team.id, setup.id, selection)
    },
    onMutate: async (selection) => {
      if (!setup || setup.id <= 0) return
      const previous = await optimisticUpdate.snapshot()
      return optimisticUpdate.registerOptimisticPatch(previous, (current) =>
        applyDefaultSetupSelection(current, setup.id, selection),
      )
    },
    onError: (_error, _variables, context) => {
      optimisticUpdate.rollback(context)
    },
    onSuccess: (data, _selection, context) => {
      optimisticUpdate.applyServerUpdate(data, context)
    },
  })

  const updateSelection = useCallback(
    (
      characterId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
    ) => {
      if (!canInteract) return
      selectionMutation.mutate({
        character_id: characterId,
        selected: updates.selected,
        role: updates.role,
        spec_id: updates.specId,
        alt_character_id: updates.characterId,
      })
    },
    [canInteract, selectionMutation],
  )

  return (
    <DefaultSetupProviders
      activity={activity}
      entries={entries}
      onUpdateSelection={updateSelection}
    >
      <div className='space-y-3'>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-baseline gap-4 min-w-0'>
            <h4 className='m-0 truncate'>
              {t('dashboard.team.plans.default-setup.title')}
            </h4>
            {enabled ? (
              <EventActivitySelectionCount activity={activity} />
            ) : null}
          </div>
          <Card className='flex items-center gap-2 h-9 px-2 shrink-0'>
            <Switch
              id='default-setup-enabled'
              checked={enabled}
              disabled={!canEdit || upsertMutation.isPending}
              onCheckedChange={(nextEnabled) =>
                upsertMutation.mutate(nextEnabled)
              }
            />
            <label
              htmlFor='default-setup-enabled'
              className={cn(
                'cursor-pointer select-none',
                (!canEdit || upsertMutation.isPending) &&
                  'pointer-events-none opacity-70',
              )}
            >
              {enabled
                ? t('dashboard.team.plans.default-setup.enabled')
                : t('dashboard.team.plans.default-setup.disabled-label')}
            </label>
          </Card>
        </div>
        <Separator />
        <p className='mb-6'>
          {t('dashboard.team.plans.default-setup.description', {
            difficulty: difficultiesLabels[difficulty],
            encounter: encounter.name,
          })}
        </p>
        <div className='relative'>
          <div
            className={cn(
              !canInteract && 'pointer-events-none',
              !enabled && 'select-none opacity-30',
            )}
          >
            <EventActivityBlock
              activity={activity}
              hideStatus
              hideEventActions
            />
          </div>
          {!enabled ? (
            <div className='absolute inset-0 flex items-center justify-center'>
              <div className='rounded-md border border-border bg-background px-4 py-2 font-medium backdrop-blur-sm text-center'>
                <div>{t('dashboard.team.plans.default-setup.disabled')}</div>
                {canEdit ? (
                  <button
                    type='button'
                    className='block mx-auto text-sm text-primary hover:text-primary/90 font-normal'
                    disabled={upsertMutation.isPending}
                    onClick={() => upsertMutation.mutate(true)}
                  >
                    {t('dashboard.team.plans.default-setup.enable')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </DefaultSetupProviders>
  )
}

export default DefaultSetupCard
